import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LEGAL_DOC_SLUGS,
  isLocaleOfCountry,
  type CountryCode,
  type LegalDocKey,
  type LegalLocale,
  type LegalPending,
} from '@capsico/contracts';
import { acceptLegal, fetchLegalDoc, fetchLegalPending } from '../api/legal';
import { getLocale, useI18n } from '../i18n';
import { showToast } from './useToast';

/*
 * W21 legal in the customer app: the blocking reconsent sheet (GET /v1/legal/pending + accept,
 * optimistic with rollback), the in-app document view and the legal links on Account.
 * Fail-open: when the legal store is off or unreachable nothing blocks the app.
 */

/** Public site (capsicoeats.com) — documents and the grievance officer page live there too. */
export const SITE_URL = (import.meta.env.VITE_SITE_URL ?? 'https://capsicoeats.com').replace(
  /\/+$/,
  '',
);

/** The document language for this country: the app language when published there, else English. */
export function legalLocaleFor(country: CountryCode, appLocale: string): LegalLocale {
  const candidate = appLocale as LegalLocale;
  return isLocaleOfCountry(country, candidate) ? candidate : 'en';
}

/** Guests: Nepali speakers are most likely in Nepal; everyone else defaults to India. */
export function guessCountry(appLocale: string): CountryCode {
  return appLocale === 'ne' ? 'NP' : 'IN';
}

export function siteLegalUrl(slug: string, country: CountryCode, lang: LegalLocale): string {
  return `${SITE_URL}/legal/${slug}?country=${country}&lang=${lang}`;
}

export function siteGrievanceUrl(country: CountryCode, lang: LegalLocale): string {
  return `${SITE_URL}/grievance?country=${country}&lang=${lang}`;
}

/** Links inside documents: site paths open on the site, http(s)/mailto/tel stay, others drop. */
export function resolveLegalHref(href: string): string | null {
  if (/^(https?:|mailto:|tel:)/i.test(href)) return href;
  if (href.startsWith('/')) return `${SITE_URL}${href}`;
  return null;
}

const PENDING_KEY = 'legal-pending';

/** Documents to accept again. Errors (store off, offline) count as "nothing pending". */
export function useLegalPending(enabled: boolean) {
  const client = useQueryClient();
  const { locale } = useI18n();
  const query = useQuery({
    queryKey: [PENDING_KEY, locale],
    enabled,
    queryFn: async (): Promise<LegalPending | null> => {
      try {
        // The server falls back to English where a document is not published in this language.
        return await fetchLegalPending(locale);
      } catch {
        return null;
      }
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: (documentIds: string[]) => acceptLegal(documentIds),
    onMutate: async (documentIds) => {
      // Optimistic: the sheet closes at once; rolled back if the server says no.
      await client.cancelQueries({ queryKey: [PENDING_KEY] });
      const key = [PENDING_KEY, getLocale()];
      const previous = client.getQueryData<LegalPending | null>(key);
      if (previous) {
        const next: LegalPending = {
          country: previous.country,
          pending: previous.pending.filter((doc) => !documentIds.includes(doc.documentId)),
        };
        client.setQueryData(key, next);
      }
      return { key, previous };
    },
    onError: (_error, _ids, context) => {
      if (context) client.setQueryData(context.key, context.previous ?? null);
      showToast(failedMessage(), { tone: 'negative' });
    },
    onSettled: () => {
      void client.invalidateQueries({ queryKey: [PENDING_KEY] });
    },
  });

  const data = query.data ?? null;
  return {
    country: data?.country ?? guessCountry(locale),
    pending: data?.pending ?? [],
    accept: (documentIds: string[]) => accept.mutate(documentIds),
    accepting: accept.isPending,
  };
}

let failedText = 'Could not save. Try again.';
/** The toast text is set by the gate (translated) before accepting. */
export function setLegalFailedMessage(text: string): void {
  failedText = text;
}
function failedMessage(): string {
  return failedText;
}

/** The reconsent sheet's own state: one agree tick, one expanded document at a time. */
export function useReconsentForm(documentIds: string[]) {
  const [agreed, setAgreed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const signature = documentIds.join(',');
  const [seen, setSeen] = useState(signature);
  if (seen !== signature) {
    // A new set of documents arrived (e.g. after a rollback): ask again.
    setSeen(signature);
    setAgreed(false);
    setOpen(null);
  }
  return {
    agreed,
    setAgreed,
    open,
    toggle: (id: string) => setOpen((current) => (current === id ? null : id)),
  };
}

/** One document for the in-app reader. */
export function useLegalDoc(slug: string, country: CountryCode, lang: LegalLocale) {
  return useQuery({
    queryKey: ['legal-doc', slug, country, lang],
    queryFn: () => fetchLegalDoc(slug, country, lang),
    staleTime: 30 * 60_000,
    retry: 1,
  });
}

export interface LegalLink {
  key: LegalDocKey | 'grievance';
  /** In-app reader path, or null for site-only pages. */
  path: string | null;
  url: string;
}

const CUSTOMER_DOCS: readonly LegalDocKey[] = [
  'terms_customer',
  'privacy',
  'refund_cancellation',
  'cookie',
];

/** Account › Legal rows: the customer documents plus the grievance officer page. */
export function useLegalLinks(country: CountryCode): LegalLink[] {
  const { locale } = useI18n();
  const lang = legalLocaleFor(country, locale);
  return useMemo(
    () => [
      ...CUSTOMER_DOCS.map((key) => {
        const slug = LEGAL_DOC_SLUGS[key];
        return {
          key,
          path: `/account/legal/${slug}?country=${country}`,
          url: siteLegalUrl(slug, country, lang),
        };
      }),
      { key: 'grievance' as const, path: null, url: siteGrievanceUrl(country, lang) },
    ],
    [country, lang],
  );
}
